import React, { useState, useEffect, useCallback } from "react";
import "../styles/AdminDashboard.css";
import {
  HiUsers,
  HiCheckCircle,
  HiPause,
  HiCreditCard,
  HiCurrencyDollar,
  HiXMark,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi2";
import { MdDashboard } from "react-icons/md";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  // Form states
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [balanceForm, setBalanceForm] = useState({
    amount: "",
    type: "credit",
    description: "",
  });

  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNumber: "",
    dateOfBirth: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  const getToken = () => localStorage.getItem("adminToken");

  // Wrap all fetch functions in useCallback
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(
        "https://shop.faithhomecares.co.uk/api/v1/admin/dashboard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setStats(data.data.stats);
        setTransactions(data.data.recentTransactions || []);
        setUsers(data.data.recentUsers || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = getToken();
      let url = `https://shop.faithhomecares.co.uk/api/v1/admin/users?page=${currentPage}&limit=20`;
      if (searchTerm) url += `&search=${searchTerm}`;
      if (filterStatus !== "all") url += `&status=${filterStatus}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [currentPage, searchTerm, filterStatus]);

  const fetchTransactions = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/transactions?page=${currentPage}&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setTransactions(data.data);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  }, [currentPage]);

  const fetchUserDetails = useCallback(async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setSelectedUser(data.data);
        setShowUserModal(true);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }, []);

  // Fixed useEffect hooks with proper dependencies
  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "transactions") {
      fetchTransactions();
    }
  }, [activeTab, fetchUsers, fetchTransactions]);

  const handleUpdateBalance = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${selectedUser.user._id}/balance`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(balanceForm),
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("Balance updated successfully");
        setShowBalanceModal(false);
        setBalanceForm({ amount: "", type: "credit", description: "" });
        fetchUserDetails(selectedUser.user._id);
        fetchDashboardStats();
      }
    } catch (error) {
      console.error("Error updating balance:", error);
      alert("Failed to update balance");
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${selectedUser.user._id}/send-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailForm),
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("Email sent successfully");
        setShowEmailModal(false);
        setEmailForm({ subject: "", message: "" });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email");
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm("Are you sure you want to suspend this user?")) return;

    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${userId}/suspend`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("User suspended successfully");
        fetchUsers();
        if (selectedUser?.user._id === userId) {
          fetchUserDetails(userId);
        }
      }
    } catch (error) {
      console.error("Error suspending user:", error);
      alert("Failed to suspend user");
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${userId}/activate`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("User activated successfully");
        fetchUsers();
        if (selectedUser?.user._id === userId) {
          fetchUserDetails(userId);
        }
      }
    } catch (error) {
      console.error("Error activating user:", error);
      alert("Failed to activate user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    )
      return;

    try {
      const token = getToken();
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/admin/users/${userId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("User deleted successfully");
        setShowUserModal(false);
        fetchUsers();
        fetchDashboardStats();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const response = await fetch(
        "https://shop.faithhomecares.co.uk/api/v1/admin/users",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: createUserForm.firstName,
            lastName: createUserForm.lastName,
            email: createUserForm.email,
            password: createUserForm.password,
            phoneNumber: createUserForm.phoneNumber,
            dateOfBirth: createUserForm.dateOfBirth,
            address: {
              street: createUserForm.street,
              city: createUserForm.city,
              state: createUserForm.state,
              zipCode: createUserForm.zipCode,
              country: createUserForm.country,
            },
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        alert("User created successfully");
        setShowCreateUserModal(false);
        setCreateUserForm({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phoneNumber: "",
          dateOfBirth: "",
          street: "",
          city: "",
          state: "",
          zipCode: "",
          country: "",
        });
        fetchUsers();
        fetchDashboardStats();
      } else {
        alert(data.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      alert("Failed to create user");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <h1 className="admin-brand">ADMIN PANEL</h1>
        </div>
        <div className="header-right">
          <div className="admin-info">
            <span className="admin-badge">Administrator</span>
            <button
              className="logout-btn"
              onClick={() => {
                localStorage.removeItem("adminToken");
                window.location.href = "/admin/login";
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="admin-nav">
        <button
          className={`admin-tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <span className="tab-icon">
            <MdDashboard />
          </span>
          Overview
        </button>
        <button
          className={`admin-tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          <span className="tab-icon">
            <HiUsers />
          </span>
          Users
        </button>
        <button
          className={`admin-tab ${activeTab === "transactions" ? "active" : ""}`}
          onClick={() => setActiveTab("transactions")}
        >
          <span className="tab-icon">
            <HiCurrencyDollar />
          </span>
          Transactions
        </button>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        {activeTab === "overview" && (
          <>
            {/* Stats Grid */}
            <section className="stats-grid">
              <div className="stat-box primary">
                <div className="stat-icon">
                  <HiUsers />
                </div>
                <div className="stat-content">
                  <h3>Total Users</h3>
                  <p className="stat-number">{stats?.totalUsers || 0}</p>
                  <span className="stat-label">
                    {stats?.activeUsers || 0} active
                  </span>
                </div>
              </div>

              <div className="stat-box success">
                <div className="stat-icon">
                  <HiCheckCircle />
                </div>
                <div className="stat-content">
                  <h3>Active Users</h3>
                  <p className="stat-number">{stats?.activeUsers || 0}</p>
                  <span className="stat-label">
                    {stats?.totalUsers > 0
                      ? (
                          (stats?.activeUsers / stats?.totalUsers) *
                          100
                        ).toFixed(1)
                      : 0}
                    % of total
                  </span>
                </div>
              </div>

              <div className="stat-box warning">
                <div className="stat-icon">
                  <HiPause />
                </div>
                <div className="stat-content">
                  <h3>Suspended</h3>
                  <p className="stat-number">{stats?.suspendedUsers || 0}</p>
                  <span className="stat-label">Requires attention</span>
                </div>
              </div>

              <div className="stat-box info">
                <div className="stat-icon">
                  <HiCreditCard />
                </div>
                <div className="stat-content">
                  <h3>Transactions</h3>
                  <p className="stat-number">{stats?.totalTransactions || 0}</p>
                  <span className="stat-label">All time</span>
                </div>
              </div>

              <div className="stat-box accent">
                <div className="stat-icon">
                  <HiCurrencyDollar />
                </div>
                <div className="stat-content">
                  <h3>Total Balance</h3>
                  <p className="stat-number">
                    {formatCurrency(stats?.totalBalance || 0)}
                  </p>
                  <span className="stat-label">System-wide</span>
                </div>
              </div>
            </section>

            {/* Recent Activity */}
            <section className="activity-section">
              <div className="section-row">
                {/* Recent Users */}
                <div className="activity-card">
                  <div className="card-header">
                    <h3>Recent Users</h3>
                    <button
                      className="view-all-btn"
                      onClick={() => setActiveTab("users")}
                    >
                      View All
                    </button>
                  </div>
                  <div className="users-list">
                    {users.slice(0, 5).map((user) => (
                      <div
                        key={user._id}
                        className="user-item"
                        onClick={() => fetchUserDetails(user._id)}
                      >
                        <div className="user-avatar">
                          {user.firstName?.[0]}
                          {user.lastName?.[0]}
                        </div>
                        <div className="user-details">
                          <h4>
                            {user.firstName} {user.lastName}
                          </h4>
                          <p>{user.email}</p>
                        </div>
                        <span
                          className={`status-dot ${user.accountStatus}`}
                        ></span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="activity-card">
                  <div className="card-header">
                    <h3>Recent Transactions</h3>
                    <button
                      className="view-all-btn"
                      onClick={() => setActiveTab("transactions")}
                    >
                      View All
                    </button>
                  </div>
                  <div className="transactions-mini-list">
                    {transactions.slice(0, 5).map((txn) => (
                      <div key={txn._id} className="transaction-mini-item">
                        <div className="txn-icon">
                          {txn.type === "credit" ? "↓" : "↑"}
                        </div>
                        <div className="txn-details">
                          <h4>{txn.description}</h4>
                          <p>
                            {txn.user?.firstName} {txn.user?.lastName}
                          </p>
                        </div>
                        <div className={`txn-amount ${txn.type}`}>
                          {txn.type === "credit" ? "+" : "-"}
                          {formatCurrency(txn.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {activeTab === "users" && (
          <>
            {/* Users Controls */}
            <div className="controls-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-group">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
                <button
                  className="create-user-btn"
                  onClick={() => setShowCreateUserModal(true)}
                >
                  + Create User
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="data-table">
              <div className="table-header">
                <span>User</span>
                <span>Email</span>
                <span>Account Number</span>
                <span>Balance</span>
                <span>Status</span>
                <span>Actions</span>
              </div>
              {users.map((user) => (
                <div key={user._id} className="table-row">
                  <div className="user-cell">
                    <div className="user-avatar small">
                      {user.firstName?.[0]}
                      {user.lastName?.[0]}
                    </div>
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                  <span>{user.email}</span>
                  <span className="mono">{user.accountNumber}</span>
                  <span className="balance">
                    {formatCurrency(user.balance || 0)}
                  </span>
                  <span>
                    <span className={`status-badge ${user.accountStatus}`}>
                      {user.accountStatus}
                    </span>
                  </span>
                  <div className="actions-cell">
                    <button
                      className="action-btn view"
                      onClick={() => fetchUserDetails(user._id)}
                    >
                      View
                    </button>
                    {user.accountStatus === "active" ? (
                      <button
                        className="action-btn suspend"
                        onClick={() => handleSuspendUser(user._id)}
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        className="action-btn activate"
                        onClick={() => handleActivateUser(user._id)}
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <HiChevronLeft /> Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next <HiChevronRight />
              </button>
            </div>
          </>
        )}

        {activeTab === "transactions" && (
          <>
            {/* Transactions Table */}
            <div className="data-table">
              <div className="table-header">
                <span>Reference</span>
                <span>User</span>
                <span>Type</span>
                <span>Amount</span>
                <span>Description</span>
                <span>Status</span>
                <span>Date</span>
              </div>
              {transactions.map((txn) => (
                <div key={txn._id} className="table-row">
                  <span className="mono">{txn.reference}</span>
                  <span>
                    {txn.user?.firstName} {txn.user?.lastName}
                  </span>
                  <span>
                    <span className={`type-badge ${txn.type}`}>{txn.type}</span>
                  </span>
                  <span className={`amount ${txn.type}`}>
                    {txn.type === "credit" ? "+" : "-"}
                    {formatCurrency(txn.amount)}
                  </span>
                  <span>{txn.description}</span>
                  <span>
                    <span className={`status-badge ${txn.status}`}>
                      {txn.status}
                    </span>
                  </span>
                  <span className="date">{formatDate(txn.createdAt)}</span>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <HiChevronLeft /> Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next <HiChevronRight />
              </button>
            </div>
          </>
        )}
      </main>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowUserModal(false)}
            >
              <HiXMark />
            </button>

            <h2>User Details</h2>

            <div className="user-profile-section">
              <div className="user-avatar large">
                {selectedUser.user.firstName?.[0]}
                {selectedUser.user.lastName?.[0]}
              </div>
              <div className="user-profile-info">
                <h3>
                  {selectedUser.user.firstName} {selectedUser.user.lastName}
                </h3>
                <p>{selectedUser.user.email}</p>
                <span
                  className={`status-badge ${selectedUser.user.accountStatus}`}
                >
                  {selectedUser.user.accountStatus}
                </span>
              </div>
            </div>

            <div className="user-details-grid">
              <div className="detail-item">
                <label>Account Number</label>
                <span className="mono">{selectedUser.user.accountNumber}</span>
              </div>
              <div className="detail-item">
                <label>Phone Number</label>
                <span>{selectedUser.user.phoneNumber || "N/A"}</span>
              </div>
              <div className="detail-item">
                <label>Balance</label>
                <span className="balance">
                  {formatCurrency(selectedUser.user.balance || 0)}
                </span>
              </div>
              <div className="detail-item">
                <label>Date of Birth</label>
                <span>
                  {selectedUser.user.dateOfBirth
                    ? formatDate(selectedUser.user.dateOfBirth)
                    : "N/A"}
                </span>
              </div>
            </div>

            <div className="action-buttons">
              <button
                className="action-btn primary"
                onClick={() => setShowBalanceModal(true)}
              >
                Update Balance
              </button>
              <button
                className="action-btn secondary"
                onClick={() => setShowEmailModal(true)}
              >
                Send Email
              </button>
              {selectedUser.user.accountStatus === "active" ? (
                <button
                  className="action-btn warning"
                  onClick={() => handleSuspendUser(selectedUser.user._id)}
                >
                  Suspend User
                </button>
              ) : (
                <button
                  className="action-btn success"
                  onClick={() => handleActivateUser(selectedUser.user._id)}
                >
                  Activate User
                </button>
              )}
              <button
                className="action-btn danger"
                onClick={() => handleDeleteUser(selectedUser.user._id)}
              >
                Delete User
              </button>
            </div>

            {/* Recent Transactions */}
            <div className="user-transactions">
              <h3>Recent Transactions</h3>
              <div className="mini-transactions-list">
                {selectedUser.recentTransactions &&
                selectedUser.recentTransactions.length > 0 ? (
                  selectedUser.recentTransactions.slice(0, 5).map((txn) => (
                    <div key={txn._id} className="mini-txn-item">
                      <div className="txn-info">
                        <span className="txn-desc">{txn.description}</span>
                        <span className="txn-date">
                          {formatDate(txn.createdAt)}
                        </span>
                      </div>
                      <span className={`txn-amount ${txn.type}`}>
                        {txn.type === "credit" ? "+" : "-"}
                        {formatCurrency(txn.amount)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="empty-message">No recent transactions</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Update Modal */}
      {showBalanceModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowBalanceModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowBalanceModal(false)}
            >
              <HiXMark />
            </button>

            <h2>Update Balance</h2>
            <form onSubmit={handleUpdateBalance}>
              <div className="form-group">
                <label>Type</label>
                <select
                  value={balanceForm.type}
                  onChange={(e) =>
                    setBalanceForm({ ...balanceForm, type: e.target.value })
                  }
                  required
                >
                  <option value="credit">Credit (Add)</option>
                  <option value="debit">Debit (Subtract)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={balanceForm.amount}
                  onChange={(e) =>
                    setBalanceForm({ ...balanceForm, amount: e.target.value })
                  }
                  required
                  placeholder="Enter amount"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={balanceForm.description}
                  onChange={(e) =>
                    setBalanceForm({
                      ...balanceForm,
                      description: e.target.value,
                    })
                  }
                  required
                  placeholder="Reason for update"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Update Balance
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowBalanceModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowEmailModal(false)}
            >
              <HiXMark />
            </button>

            <h2>Send Email</h2>
            <form onSubmit={handleSendEmail}>
              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  required
                  placeholder="Email subject"
                />
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, message: e.target.value })
                  }
                  required
                  rows="6"
                  placeholder="Email message"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Send Email
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEmailModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateUserModal(false)}
        >
          <div
            className="modal-content large create-user-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowCreateUserModal(false)}
            >
              <HiXMark />
            </button>

            <h2>Create New User</h2>

            <form onSubmit={handleCreateUser} className="create-user-form">
              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={createUserForm.firstName}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          firstName: e.target.value,
                        })
                      }
                      required
                      placeholder="John"
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={createUserForm.lastName}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          lastName: e.target.value,
                        })
                      }
                      required
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={createUserForm.email}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          email: e.target.value,
                        })
                      }
                      required
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={createUserForm.phoneNumber}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          phoneNumber: e.target.value,
                        })
                      }
                      required
                      placeholder="+27123456789"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          password: e.target.value,
                        })
                      }
                      required
                      placeholder="Minimum 8 characters"
                      minLength="8"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      value={createUserForm.dateOfBirth}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          dateOfBirth: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Address Information</h3>
                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    value={createUserForm.street}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        street: e.target.value,
                      })
                    }
                    required
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={createUserForm.city}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          city: e.target.value,
                        })
                      }
                      required
                      placeholder="Johannesburg"
                    />
                  </div>

                  <div className="form-group">
                    <label>State/Province *</label>
                    <input
                      type="text"
                      value={createUserForm.state}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          state: e.target.value,
                        })
                      }
                      required
                      placeholder="Gauteng"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Zip Code *</label>
                    <input
                      type="text"
                      value={createUserForm.zipCode}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          zipCode: e.target.value,
                        })
                      }
                      required
                      placeholder="2000"
                    />
                  </div>

                  <div className="form-group">
                    <label>Country *</label>
                    <input
                      type="text"
                      value={createUserForm.country}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          country: e.target.value,
                        })
                      }
                      required
                      placeholder="South Africa"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateUserModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
